
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertCircle, AlertTriangle, CheckCircle, FileText, ClipboardList } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { MaintenanceAlertData } from '../types/maintenanceTypes';

interface MaintenanceAlertCardProps {
  alert: MaintenanceAlertData;
  index: number;
}

const MaintenanceAlertCard: React.FC<MaintenanceAlertCardProps> = ({ alert, index }) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleViewReport = (deviceId: string, deviceType: string) => {
    navigate(`/device-report/${deviceId}/${deviceType}`);
  };

  const handleCreateWorkOrder = (deviceId: string, assetName: string) => {
    toast({
      title: "Work Order Created",
      description: `Work order for ${assetName} (${deviceId}) has been created.`,
    });
  };

  // Determine status icon
  const statusIcon = alert.status === 'Alert' ? 
    <AlertCircle className="h-5 w-5 text-red-600" /> :
    alert.status === 'Warning' ? 
    <AlertTriangle className="h-5 w-5 text-amber-500" /> :
    <CheckCircle className="h-5 w-5 text-emerald-500" />;

  const assetName = alert.assetName || alert.title;
  const assetType = alert.assetType || alert.deviceType;
  const location = alert.location || alert.facility || 'Unknown Location';

  return (
    <Card key={`${alert.deviceId}-${index}`} className="overflow-hidden mb-4">
      <CardContent className="p-0">
        <div className="flex gap-4 p-4">
          <div className="flex-none mt-1">
            {statusIcon}
          </div>
          
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-3">
              <div>
                <h3 className="font-medium text-base">{assetName}</h3>
                <div className="flex flex-wrap gap-2 mt-1">
                  <Badge variant="outline" className="bg-gray-100 dark:bg-gray-800">
                    {assetType}
                  </Badge>
                  <Badge variant="outline" className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                    {location}
                  </Badge>
                </div>
              </div>
              
              <div className="text-sm text-muted-foreground">
                {alert.status === 'Good' ? (
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-muted-foreground">Next Inspection:</span>
                    <span className="font-medium">{alert.nextInspection}</span>
                  </div>
                ) : (
                  <div className="text-red-500 font-medium">
                    {alert.issue}
                  </div>
                )}
              </div>
            </div>
            
            {alert.aiPrediction && (
              <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-md border border-blue-100 dark:border-blue-800 mb-3">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      <span className="text-xs font-medium text-blue-700 dark:text-blue-300">AI PREDICTION</span>
                    </div>
                    <p className="text-sm text-blue-900 dark:text-blue-50">{alert.aiPrediction}</p>
                  </div>
                  {alert.confidence && (
                    <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-800/40 dark:text-blue-300">
                      Confidence: {alert.confidence}%
                    </Badge>
                  )}
                </div>
                
                {alert.estimatedImpact && (
                  <div className="mt-2 pt-2 border-t border-blue-200 dark:border-blue-800">
                    <p className="text-xs text-blue-800 dark:text-blue-200">
                      <span className="font-medium">Estimated Impact:</span> {alert.estimatedImpact}
                    </p>
                  </div>
                )}
              </div>
            )}
            
            <div className="flex justify-between items-center mt-1">
              <div className="text-xs text-muted-foreground">
                Report date: {new Date(alert.reportDate).toLocaleDateString()}
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="flex items-center"
                  onClick={() => handleViewReport(alert.deviceId, alert.deviceType)}
                >
                  <FileText className="mr-1 h-3.5 w-3.5" />
                  View Report
                </Button>
                
                {alert.status === 'Alert' && (
                  <Button 
                    size="sm"
                    className="flex items-center"
                    onClick={() => handleCreateWorkOrder(alert.deviceId, assetName)}
                  >
                    <ClipboardList className="mr-1 h-3.5 w-3.5" />
                    Create Work Order
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MaintenanceAlertCard;
